import { CheckIcon, CopyIcon, Share2Icon } from '@radix-ui/react-icons'
import { useEffect, useRef, useState } from 'react'
import { Icon } from '../atoms/icon.tsx'

/** Facebook's share dialog for `url`; no app id or SDK needed */
const facebookShareUrl = (url: string) =>
	`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`

/**
 * Share a page: Facebook's share dialog (a plain link, so it works without
 * JavaScript), then the device's own share sheet where there is one —
 * Messenger, WhatsApp, SMS … — or else copying the link. What the post
 * shows comes from the page's Open Graph tags.
 */
export function ShareLinks({
	url,
	title,
	linkClassName,
}: {
	/** the page's absolute URL */
	url: string
	title: string
	linkClassName?: string
}) {
	// the share sheet is known only in the browser; the server renders "copy"
	const [canShare, setCanShare] = useState(false)
	useEffect(() => setCanShare(typeof navigator.share === 'function'), [])
	const [copied, setCopied] = useState(false)
	const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
	useEffect(() => () => clearTimeout(timer.current), [])

	const share = async () => {
		try {
			await navigator.share({ title, url })
		} catch {
			// closed the sheet without sharing: nothing to do
		}
	}

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(url)
			setCopied(true)
			clearTimeout(timer.current)
			timer.current = setTimeout(() => setCopied(false), 2500)
		} catch {
			// no clipboard (old browser, insecure origin): show it to copy by hand
			window.prompt('Kopier lenkja:', url)
		}
	}

	return (
		<>
			<a
				href={facebookShareUrl(url)}
				target="_blank"
				rel="noopener noreferrer"
				className={linkClassName}
			>
				<Icon name="facebook" aria-hidden="true" /> Del på Facebook
			</a>
			{canShare ? (
				<button type="button" className={linkClassName} onClick={share}>
					<Share2Icon aria-hidden="true" /> Del
				</button>
			) : (
				<button type="button" className={linkClassName} onClick={copy}>
					{copied ? (
						<>
							<CheckIcon aria-hidden="true" /> Kopiert
						</>
					) : (
						<>
							<CopyIcon aria-hidden="true" /> Kopier lenkje
						</>
					)}
					<span className="sr-only" aria-live="polite">
						{copied ? 'Lenkja er kopiert' : ''}
					</span>
				</button>
			)}
		</>
	)
}
